/**
 * @fileoverview System-web import-run history grid for cross-tenant administrative review.
 * @module system-web/import-runs-page
 *
 * Responsibilities:
 * - Render import-run history from the local-first system Dexie cache.
 * - Refresh run snapshots through the system import-run service boundary.
 * - Route commit, cancel, delete, and detail navigation through service-mediated actions.
 *
 * Dependencies:
 * - `../services/systemImportRunService` for local-first reads and online run mutations.
 * - `../services/tenantService` for department filter options.
 * - `react-router-dom` for run detail navigation.
 *
 * Architecture:
 * - Dexie is the UI source of truth for grid rendering.
 * - API access remains isolated behind service functions for online import exceptions.
 *
 * Notes:
 * - Upload, commit, cancel, and refresh operations are inherently online import operations.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, History, Eye, XCircle, CheckCircle, Trash2, AlertCircle, X } from 'lucide-react';
import type { ImportRun, ImportRunStatus } from '@firesquirrel/local-db';
import { listAllImportRuns, listImportRuns, refreshImportRuns, cancelImportRun, commitImportRun, deleteImportRun, isImportRunActive } from '../services/systemImportRunService';
import { refreshAllImportRuns } from '../services/systemImportRunService';
import { listTenants } from '../services/tenantService';

const STATUS_COLORS: Record<ImportRunStatus, string> = {
  draft:             'bg-slate-100 text-slate-700',
  uploading:         'bg-sky-100 text-sky-700',
  queued:            'bg-gray-100 text-gray-700',
  parsing:           'bg-blue-100 text-blue-700',
  parsed:            'bg-indigo-100 text-indigo-700',
  'dry-run':         'bg-purple-100 text-purple-700',
  'awaiting-commit': 'bg-yellow-100 text-yellow-800',
  committing:        'bg-blue-100 text-blue-700',
  finalizing:        'bg-teal-100 text-teal-700',
  completed:         'bg-emerald-100 text-emerald-700',
  failed:            'bg-red-100 text-red-700',
  cancelled:         'bg-gray-100 text-gray-500',
};

const STATUS_LABELS: Record<ImportRunStatus, string> = {
  draft:             'Draft',
  uploading:         'Uploading',
  queued:            'Queued',
  parsing:           'Parsing',
  parsed:            'Preview Ready',
  'dry-run':         'Dry Run',
  'awaiting-commit': 'Awaiting Commit',
  committing:        'Committing',
  finalizing:        'Finalizing',
  completed:         'Completed',
  failed:            'Failed',
  cancelled:         'Cancelled',
};

const CANCELLABLE = new Set<ImportRunStatus>(['queued', 'parsing', 'dry-run', 'awaiting-commit']);
const DELETABLE = new Set<ImportRunStatus>(['draft', 'uploading', 'parsed', 'failed', 'cancelled', 'completed', 'awaiting-commit']);

function fmtDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function summarize(run: ImportRun): string {
  if (!run.summary) return '—';
  const created = run.summary.created ?? 0;
  const updated = run.summary.updated ?? 0;
  const excluded = run.summary.excluded ?? {
    outOfJurisdiction: 0,
    missingCoordinates: 0,
    duplicateInsertOnly: 0,
    missingMatchKey: 0,
  };
  const failedByKind = run.summary.failedByKind ?? {
    transform: 0,
    entityBuild: 0,
  };
  const excludedTotal =
    (excluded.outOfJurisdiction ?? 0) +
    (excluded.missingCoordinates ?? 0) +
    (excluded.duplicateInsertOnly ?? 0) +
    (excluded.missingMatchKey ?? 0);
  const failedTotal = (failedByKind.transform ?? 0) + (failedByKind.entityBuild ?? 0);
  return `${created} cr · ${updated} up · ${excludedTotal} ex · ${failedTotal} err`;
}

/**
 * @component ImportRunsPage
 * Purpose:     Renders the cross-tenant import-run history grid for system-web
 *              administrative review, with department filtering and run actions.
 * Props:       (none — route component)
 * Behavior:    Loads runs via listAllImportRuns / listImportRuns; supports
 *              refresh, cancel, commit, and delete actions via service functions;
 *              navigates to detail page on row click.
 * Data Source: local (Dexie via systemImportRunService)
 * Constraints: no direct API calls; online operations (commit, cancel) go through
 *              service functions as documented online-only exceptions
 * Performance: grid virtualized by GridPageShell; department filter via tenantService
 */
export default function ImportRunsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [runs, setRuns] = useState<ImportRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<ImportRunStatus | ''>('');
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [committing, setCommitting] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tenants, setTenants] = useState<any[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');

  const readRuns = useCallback(() => {
    return selectedTenantId
      ? listImportRuns(selectedTenantId)
      : listAllImportRuns();
  }, [selectedTenantId]);

  const refreshRuns = useCallback(() => {
    return selectedTenantId
      ? refreshImportRuns(selectedTenantId)
      : refreshAllImportRuns();
  }, [selectedTenantId]);

  const buildRunDetailUrl = useCallback((run: ImportRun) => {
    const runTenantId = selectedTenantId || ((run as any).tenantId ?? '');
    return runTenantId
      ? `/import-runs/${run._id}?tenantId=${encodeURIComponent(runTenantId)}`
      : `/import-runs/${run._id}`;
  }, [selectedTenantId]);

  const load = useCallback(async (mode: 'local' | 'refresh' = 'local') => {
    setLoading(true);
    try {
      setRuns(await (mode === 'refresh' ? refreshRuns() : readRuns()));
    }
    catch { setRuns([]); }
    finally { setLoading(false); }
  }, [readRuns, refreshRuns]);

  useEffect(() => {
    listTenants()
      .then(rows => {
        setTenants(rows);
        const fromQuery = searchParams.get('tenantId');
        if (fromQuery && rows.some(r => r._id === fromQuery)) {
          setSelectedTenantId(fromQuery);
          return;
        }
        if (rows.length === 1) setSelectedTenantId(rows[0]._id);
      })
      .catch(() => setTenants([]));
  }, [searchParams]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!runs.some(run => isImportRunActive(run))) return;

    const timer = window.setInterval(() => {
      refreshRuns()
        .then(setRuns)
        .catch(() => { /* ignore transient refresh errors */ });
    }, 5000);

    return () => window.clearInterval(timer);
  }, [refreshRuns, runs]);

  const handleCommit = async (run: ImportRun) => {
    const tenantId = selectedTenantId || ((run as any).tenantId ?? '');
    if (!tenantId) return;
    setCommitting(run._id);
    try {
      setErrorMessage(null);
      await commitImportRun(tenantId, run._id);
      await load('refresh');
    }
    catch (e: any) {
      setErrorMessage(e.message ?? 'Commit failed. Please try again.');
    }
    finally { setCommitting(null); }
  };

  const handleCancel = async (run: ImportRun) => {
    const tenantId = selectedTenantId || ((run as any).tenantId ?? '');
    if (!tenantId) return;
    setCancelling(run._id);
    try { await cancelImportRun(tenantId, run._id); await load('refresh'); }
    finally { setCancelling(null); }
  };

  const handleDelete = async (run: ImportRun) => {
    setDeleting(run._id);
    try {
      await deleteImportRun(run._id);
      await load('local');
    } finally {
      setDeleting(null);
    }
  };

  const filtered = runs.filter(r => {
    if (filterStatus && r.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = ((r as any).importName ?? r.filename ?? '').toLowerCase();
      if (!name.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {errorMessage && (
        <div className="mx-6 mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="flex-1">{errorMessage}</span>
          <button type="button" onClick={() => setErrorMessage(null)} className="p-0.5 hover:bg-red-100 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="shrink-0 px-6 py-4 border-b bg-white flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1 justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Import Runs</h1>
            <p className="text-sm text-gray-500 mt-0.5">History of import runs filtered by department.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-gray-700">Department Filter</label>
            <select
              className="border border-gray-300 rounded-md px-2 py-1 text-xs text-gray-800 min-w-[220px]"
              value={selectedTenantId}
              onChange={e => setSelectedTenantId(e.target.value)}
            >
              <option value="">All departments</option>
              {tenants.map(t => (
                <option key={t._id} value={t._id}>{t.name || t.slug || t._id}</option>
              ))}
            </select>
          </div>
        </div>
        <button type="button" onClick={() => navigate('/import-runs/new')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors">
          <Upload className="h-3.5 w-3.5" /> New Import
        </button>
      </div>

      <div className="shrink-0 px-6 py-3 border-b bg-gray-50 flex items-center gap-3 flex-wrap">
        <input type="text" placeholder="Search runs…" value={search} onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-xs text-gray-700 w-52 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        <select className="border border-gray-300 rounded-md px-2 py-1.5 text-xs text-gray-700"
          value={filterStatus} onChange={e => setFilterStatus(e.target.value as ImportRunStatus | '')}>
          <option value="">All statuses</option>
          {(Object.keys(STATUS_LABELS) as ImportRunStatus[]).map(s => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-gray-700">Department</label>
          <select
            className="border border-gray-300 rounded-md px-2 py-1.5 text-xs text-gray-800 min-w-[220px]"
            value={selectedTenantId}
            onChange={e => setSelectedTenantId(e.target.value)}
          >
            <option value="">All departments</option>
            {tenants.map(t => (
              <option key={t._id} value={t._id}>{t.name || t.slug || t._id}</option>
            ))}
          </select>
        </div>
        <button type="button" onClick={() => void load('refresh')} className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-1 hover:bg-gray-100">
          Refresh
        </button>
        {runs.some(run => isImportRunActive(run)) && (
          <span className="text-[11px] text-blue-500">Auto-refreshing active imports…</span>
        )}
        <span className="ml-auto text-[11px] text-gray-400">{filtered.length} run{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-sm text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3 text-gray-400">
            <History className="h-8 w-8 text-gray-300" />
            <p className="text-sm">{search || filterStatus ? 'No runs match your filters.' : 'No import runs yet.'}</p>
            {!search && !filterStatus && (
              <button type="button" onClick={() => navigate('/import-runs/new')}
                className="text-xs text-orange-600 hover:underline">
                Start a new import
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs text-left text-gray-700">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2.5 font-semibold text-gray-600 w-52">Department / Scope</th>
                  <th className="px-4 py-2.5 font-semibold text-gray-600">Import Name</th>
                  <th className="px-4 py-2.5 font-semibold text-gray-600 w-36">Status</th>
                  <th className="px-4 py-2.5 font-semibold text-gray-600 w-20 text-center">Dry Run</th>
                  <th className="px-4 py-2.5 font-semibold text-gray-600 w-44">Started</th>
                  <th className="px-4 py-2.5 font-semibold text-gray-600 w-44">Completed</th>
                  <th className="px-4 py-2.5 font-semibold text-gray-600">Summary</th>
                  <th className="px-4 py-2.5 font-semibold text-gray-600 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((run, i) => {
                  return (
                  <tr key={run._id} className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${i % 2 === 1 ? 'bg-gray-50/40' : ''}`}
                    onClick={() => navigate(buildRunDetailUrl(run))}>
                    <td className="px-4 py-2.5 text-gray-500">
                      <div className="truncate max-w-[180px]">{(run as any).tenantName ?? (run as any).tenantSlug ?? (run as any).tenantId ?? 'System Data'}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900 truncate max-w-[220px]">{(run as any).importName ?? run.filename}</div>
                      <div className="text-[11px] text-gray-400 truncate max-w-[220px]">{run.filename}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[run.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABELS[run.status] ?? run.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {run.isDryRun
                        ? <span className="text-[11px] bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-1.5 py-0.5">Yes</span>
                        : <span className="text-[11px] text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">{fmtDate(run.startedAt)}</td>
                    <td className="px-4 py-2.5 text-gray-500">{fmtDate(run.completedAt)}</td>
                    <td className="px-4 py-2.5 text-gray-600 font-mono">{summarize(run)}</td>
                    <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => navigate(buildRunDetailUrl(run))}
                          className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        {run.status === 'awaiting-commit' && (
                          <button type="button" onClick={() => handleCommit(run)}
                            disabled={committing === run._id}
                            title="Commit this import"
                            className="p-1 rounded text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
                            <CheckCircle className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {CANCELLABLE.has(run.status) && (
                          <button type="button" onClick={() => handleCancel(run)}
                            disabled={cancelling === run._id}
                            className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40">
                            <XCircle className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {DELETABLE.has(run.status) && (
                          <button
                            type="button"
                            onClick={() => handleDelete(run)}
                            disabled={deleting === run._id}
                            title="Delete this tracked import attempt"
                            className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-40"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
